import { useState, useEffect, useMemo, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { ScrollArea } from '../components/ui/scroll-area';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import {
  CalendarDays, ChevronLeft, ChevronRight, Loader2, Plus,
  Instagram, Linkedin, Twitter, Youtube, FileText, CheckCircle2,
  Clock, MoreVertical, CalendarX, CalendarCheck, Edit2, Send, RotateCw, AlertTriangle
} from 'lucide-react';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '../components/ui/dropdown-menu';
import { SchedulePostDialog } from '../components/SchedulePostDialog';
import { PostStatusBadge } from '../components/PostStatusBadge';
import { ClientFilterPill, ClientBadge } from '../components/ClientFilterPill';
import { useClients } from '../hooks/useClients';
import { toast } from 'sonner';

const PLATFORMS = ['Instagram', 'LinkedIn', 'Bluesky', 'Facebook', 'YouTube Shorts'];

const platformIcon = (platform) => {
  const cls = 'w-3.5 h-3.5';
  switch (platform) {
    case 'Instagram': return <Instagram className={cls} />;
    case 'LinkedIn': return <Linkedin className={cls} />;
    case 'TikTok': return <span className="text-[9px] font-bold leading-none">TT</span>;
    case 'Bluesky': return <Twitter className={cls} />;
    case 'Facebook': return <span className="text-[9px] font-bold leading-none">FB</span>;
    case 'YouTube Shorts': return <Youtube className={cls} />;
    default: return <FileText className={cls} />;
  }
};

const platformAccent = (platform) => {
  switch (platform) {
    case 'Instagram': return 'bg-pink-50 text-pink-700 border-pink-200';
    case 'LinkedIn': return 'bg-sky-50 text-sky-700 border-sky-200';
    case 'TikTok': return 'bg-zinc-100 text-zinc-700 border-zinc-300';
    case 'Bluesky': return 'bg-zinc-100 text-zinc-700 border-zinc-300';
    case 'Facebook': return 'bg-blue-50 text-blue-700 border-blue-200';
    case 'YouTube Shorts': return 'bg-red-50 text-red-700 border-red-200';
    default: return 'bg-zinc-100 text-zinc-700 border-zinc-200';
  }
};

const startOfMonth = (d) => new Date(d.getFullYear(), d.getMonth(), 1);
const startOfCalendar = (d) => {
  const start = startOfMonth(d);
  const offset = start.getDay(); // 0=Sun
  return new Date(start.getFullYear(), start.getMonth(), start.getDate() - offset);
};
const addDays = (d, n) => {
  const x = new Date(d);
  x.setDate(x.getDate() + n);
  return x;
};
const sameDay = (a, b) => a.toDateString() === b.toDateString();
const localISODate = (d) =>
  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
const fmtMonth = (d) => d.toLocaleDateString(undefined, { month: 'long', year: 'numeric' });
const fmtDayLong = (d) => d.toLocaleDateString(undefined, { weekday: 'long', month: 'long', day: 'numeric' });

export const CalendarPage = () => {
  const { api } = useAuth();
  const [cursor, setCursor] = useState(() => new Date()); // any date in viewed month
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterPlatform, setFilterPlatform] = useState('all');
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterClient, setFilterClient] = useState('all');
  const { clients, byId: clientsById } = useClients();
  const [selectedDay, setSelectedDay] = useState(() => new Date());
  const [scheduleTarget, setScheduleTarget] = useState(null);
  const today = useMemo(() => new Date(), []);

  const calendarStart = useMemo(
    () => startOfCalendar(new Date(cursor.getFullYear(), cursor.getMonth(), 1)),
    [cursor]
  );
  const calendarStartIso = useMemo(() => calendarStart.toISOString(), [calendarStart]);
  const calendarEndIso = useMemo(() => addDays(calendarStart, 42).toISOString(), [calendarStart]);
  const calendarDays = useMemo(
    () => Array.from({ length: 42 }, (_, i) => addDays(calendarStart, i)),
    [calendarStart]
  );

  const fetchCalendar = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        start: calendarStartIso,
        end: calendarEndIso,
      });
      if (filterClient && filterClient !== 'all') params.append('client_id', filterClient);
      const res = await api.get(`/posts/calendar?${params.toString()}`);
      setPosts(res.data.posts || []);
    } catch (error) {
      toast.error('Failed to load calendar');
    } finally {
      setLoading(false);
    }
    // api intentionally omitted: it's recreated each render but the URL fully captures intent
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [calendarStartIso, calendarEndIso, filterClient]);

  useEffect(() => { fetchCalendar(); }, [fetchCalendar]);

  const filteredPosts = useMemo(() => {
    return posts.filter((p) => {
      if (filterPlatform !== 'all' && p.platform !== filterPlatform) return false;
      if (filterStatus !== 'all' && p.status !== filterStatus) return false;
      return true;
    });
  }, [posts, filterPlatform, filterStatus]);

  const postsByDay = useMemo(() => {
    const map = new Map();
    for (const p of filteredPosts) {
      if (!p.scheduled_for) continue;
      const key = new Date(p.scheduled_for).toDateString();
      if (!map.has(key)) map.set(key, []);
      map.get(key).push(p);
    }
    for (const arr of map.values()) {
      arr.sort((a, b) => new Date(a.scheduled_for) - new Date(b.scheduled_for));
    }
    return map;
  }, [filteredPosts]);

  const selectedPosts = useMemo(
    () => postsByDay.get(selectedDay.toDateString()) || [],
    [postsByDay, selectedDay]
  );

  const goPrev = () => setCursor(new Date(cursor.getFullYear(), cursor.getMonth() - 1, 1));
  const goNext = () => setCursor(new Date(cursor.getFullYear(), cursor.getMonth() + 1, 1));
  const goToday = () => {
    const t = new Date();
    setCursor(t);
    setSelectedDay(t);
  };

  const handleScheduled = (updated) => {
    setPosts((prev) => {
      const others = prev.filter((p) => p.id !== updated.id);
      return updated.scheduled_for ? [...others, updated] : others;
    });
  };

  const handleMarkPublished = async (post) => {
    try {
      const res = await api.post(`/posts/${post.id}/mark-published`);
      setPosts((prev) => prev.map((p) => (p.id === post.id ? res.data : p)));
      toast.success('Marked as published');
    } catch {
      toast.error('Failed to update');
    }
  };

  const handleUnschedule = async (post) => {
    try {
      await api.post(`/posts/${post.id}/unschedule`);
      setPosts((prev) => prev.filter((p) => p.id !== post.id));
      toast.success('Removed from calendar');
    } catch {
      toast.error('Failed to unschedule');
    }
  };

  const handlePublishNow = async (post) => {
    if (!window.confirm(`Publish to ${post.platform} right now?`)) return;
    const promise = api.post(`/posts/${post.id}/publish-now`).then((res) => {
      const u = res.data;
      setPosts((prev) => prev.map((p) => (p.id === u.id ? u : p)));
      if (u.publish_status === 'published') return u;
      throw new Error(u.publish_error || 'Publish failed');
    });
    toast.promise(promise, {
      loading: `Publishing to ${post.platform}…`,
      success: 'Published!',
      error: (err) => err?.message || 'Publish failed',
    });
  };

  const handleRetryPublish = async (post) => {
    const promise = api.post(`/posts/${post.id}/retry-publish`).then((res) => {
      const u = res.data;
      setPosts((prev) => prev.map((p) => (p.id === u.id ? u : p)));
      if (u.publish_status === 'published') return u;
      throw new Error(u.publish_error || 'Retry failed');
    });
    toast.promise(promise, { loading: 'Retrying…', success: 'Published!', error: (e) => e?.message || 'Retry failed' });
  };

  const totalScheduled = posts.filter((p) => p.status === 'scheduled').length;
  const totalPublished = posts.filter((p) => p.status === 'published').length;
  const totalFailed = posts.filter((p) => p.status === 'failed').length;

  return (
    <div className="space-y-6" data-testid="calendar-page">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold tracking-tight text-zinc-900 flex items-center gap-2">
            <CalendarDays className="w-6 h-6 text-indigo-600" />
            Content Calendar
          </h1>
          <p className="text-zinc-500 text-sm mt-0.5">
            {totalScheduled} scheduled • {totalPublished} published this month{totalFailed > 0 ? ` • ${totalFailed} failed` : ''}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Select value={filterPlatform} onValueChange={setFilterPlatform}>
            <SelectTrigger className="w-[150px]" data-testid="calendar-filter-platform">
              <SelectValue placeholder="Platform" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All platforms</SelectItem>
              {PLATFORMS.map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={filterStatus} onValueChange={setFilterStatus}>
            <SelectTrigger className="w-[140px]" data-testid="calendar-filter-status">
              <SelectValue placeholder="Status" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All status</SelectItem>
              <SelectItem value="scheduled">Scheduled</SelectItem>
              <SelectItem value="published">Published</SelectItem>
              <SelectItem value="failed">Failed</SelectItem>
            </SelectContent>
          </Select>
          <ClientFilterPill
            value={filterClient}
            onChange={setFilterClient}
            clients={clients}
          />
        </div>
      </div>

      <div className="grid lg:grid-cols-[1fr_360px] gap-6">
        {/* Calendar grid */}
        <Card className="border-zinc-200">
          <CardContent className="p-4 sm:p-6">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2">
                <Button variant="outline" size="icon" onClick={goPrev} data-testid="calendar-prev-btn">
                  <ChevronLeft className="w-4 h-4" />
                </Button>
                <Button variant="outline" size="icon" onClick={goNext} data-testid="calendar-next-btn">
                  <ChevronRight className="w-4 h-4" />
                </Button>
                <Button variant="ghost" size="sm" onClick={goToday} data-testid="calendar-today-btn">
                  Today
                </Button>
              </div>
              <h2 className="text-lg font-semibold text-zinc-900" data-testid="calendar-month-label">
                {fmtMonth(cursor)}
              </h2>
              <div className="w-[140px]" />
            </div>

            {loading ? (
              <div className="flex items-center justify-center py-20">
                <Loader2 className="w-8 h-8 animate-spin text-indigo-600" />
              </div>
            ) : (
              <>
                <div className="grid grid-cols-7 gap-1 mb-1">
                  {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map((d) => (
                    <div key={d} className="text-center text-xs font-medium text-zinc-500 py-2">
                      {d}
                    </div>
                  ))}
                </div>
                <div className="grid grid-cols-7 gap-1">
                  {calendarDays.map((d, i) => {
                    const inMonth = d.getMonth() === cursor.getMonth();
                    const isToday = sameDay(d, today);
                    const isSelected = sameDay(d, selectedDay);
                    const dayPosts = postsByDay.get(d.toDateString()) || [];
                    return (
                      <button
                        key={i}
                        type="button"
                        onClick={() => setSelectedDay(d)}
                        className={`min-h-[88px] rounded-lg border text-left p-2 transition-colors flex flex-col gap-1 ${
                          isSelected
                            ? 'border-indigo-500 bg-indigo-50/60'
                            : 'border-zinc-200 hover:border-zinc-300 hover:bg-zinc-50'
                        } ${!inMonth ? 'opacity-40' : ''}`}
                        data-testid={`calendar-day-${localISODate(d)}`}
                      >
                        <div className="flex items-center justify-between">
                          <span
                            className={`text-xs font-semibold ${
                              isToday ? 'bg-indigo-600 text-white rounded-full w-5 h-5 flex items-center justify-center' :
                              isSelected ? 'text-indigo-700' : 'text-zinc-700'
                            }`}
                          >
                            {d.getDate()}
                          </span>
                          {dayPosts.length > 0 && (
                            <span className="text-[10px] font-medium text-zinc-500">
                              {dayPosts.length}
                            </span>
                          )}
                        </div>
                        <div className="flex flex-col gap-0.5 overflow-hidden">
                          {dayPosts.slice(0, 3).map((p) => {
                            const c = p.client_id ? clientsById[p.client_id] : null;
                            return (
                              <div
                                key={p.id}
                                className={`text-[10px] px-1.5 py-0.5 rounded border truncate flex items-center gap-1 ${platformAccent(p.platform)} ${
                                  p.status === 'published' ? 'opacity-60 line-through' :
                                  p.status === 'failed' ? 'ring-1 ring-red-300 bg-red-50 text-red-700 border-red-200' : ''
                                }`}
                              >
                                {p.status === 'failed' ? <AlertTriangle className="w-3 h-3 shrink-0" /> : platformIcon(p.platform)}
                                {c && (
                                  <span
                                    className="inline-block w-1.5 h-1.5 rounded-full shrink-0"
                                    style={{ background: c.brand_color }}
                                    title={c.name}
                                  />
                                )}
                                <span className="truncate">{p.post_title || p.caption?.slice(0, 30)}</span>
                              </div>
                            );
                          })}
                          {dayPosts.length > 3 && (
                            <span className="text-[10px] text-zinc-500">+{dayPosts.length - 3} more</span>
                          )}
                        </div>
                      </button>
                    );
                  })}
                </div>
              </>
            )}
          </CardContent>
        </Card>

        {/* Day detail panel */}
        <Card className="border-zinc-200" data-testid="calendar-day-panel">
          <CardContent className="p-4">
            <div className="flex items-center justify-between mb-3">
              <div>
                <h3 className="font-semibold text-zinc-900">{fmtDayLong(selectedDay)}</h3>
                <p className="text-xs text-zinc-500">
                  {selectedPosts.length === 0 ? 'No posts scheduled' : `${selectedPosts.length} post${selectedPosts.length === 1 ? '' : 's'}`}
                </p>
              </div>
              <Button
                size="sm"
                variant="outline"
                onClick={() => window.location.assign('/posts')}
                data-testid="calendar-add-post-btn"
              >
                <Plus className="w-4 h-4 mr-1" />
                Add
              </Button>
            </div>
            <ScrollArea className="h-[480px]">
              {selectedPosts.length === 0 ? (
                <div className="text-center py-12 text-sm text-zinc-500">
                  Pick a day with scheduled content, or schedule a post from
                  <span className="text-indigo-600 font-medium"> My Posts</span>.
                </div>
              ) : (
                <div className="space-y-2 pr-2">
                  {selectedPosts.map((p) => (
                    <div
                      key={p.id}
                      className="rounded-lg border border-zinc-200 p-3 hover:border-zinc-300 transition-colors"
                      data-testid={`calendar-post-${p.id}`}
                    >
                      <div className="flex items-start justify-between gap-2 mb-1.5">
                        <div className="flex items-center gap-2 flex-wrap">
                          <Badge variant="outline" className={`flex items-center gap-1 ${platformAccent(p.platform)}`}>
                            {platformIcon(p.platform)}
                            <span className="text-[10px]">{p.platform}</span>
                          </Badge>
                          <PostStatusBadge post={p} size="sm" showTime={p.status !== 'scheduled'} />
                          {p.status === 'scheduled' && (
                            <span className="text-[10px] text-zinc-500">
                              {new Date(p.scheduled_for).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                            </span>
                          )}
                          <ClientBadge clientId={p.client_id} clientsById={clientsById} />
                        </div>
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost" size="icon" className="h-7 w-7">
                              <MoreVertical className="w-4 h-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuItem onClick={() => setScheduleTarget(p)}>
                              <Edit2 className="w-4 h-4 mr-2" />
                              Reschedule
                            </DropdownMenuItem>
                            {p.status !== 'published' && (
                              <DropdownMenuItem
                                onClick={() => handlePublishNow(p)}
                                data-testid={`calendar-publish-now-${p.id}`}
                              >
                                <Send className="w-4 h-4 mr-2" />
                                Publish now
                              </DropdownMenuItem>
                            )}
                            {p.status === 'failed' && (
                              <DropdownMenuItem
                                onClick={() => handleRetryPublish(p)}
                                data-testid={`calendar-retry-${p.id}`}
                              >
                                <RotateCw className="w-4 h-4 mr-2" />
                                Retry publish
                              </DropdownMenuItem>
                            )}
                            {p.status !== 'published' && p.status !== 'failed' && (
                              <DropdownMenuItem onClick={() => handleMarkPublished(p)}>
                                <CalendarCheck className="w-4 h-4 mr-2" />
                                Mark as published
                              </DropdownMenuItem>
                            )}
                            <DropdownMenuItem
                              onClick={() => handleUnschedule(p)}
                              className="text-red-600"
                            >
                              <CalendarX className="w-4 h-4 mr-2" />
                              Unschedule
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </div>
                      <p className="text-sm font-medium text-zinc-900 line-clamp-1">
                        {p.post_title || '(no title)'}
                      </p>
                      <p className="text-xs text-zinc-600 mt-0.5 line-clamp-2">{p.caption}</p>
                      {p.status === 'failed' && p.publish_error && (
                        <p className="text-[11px] text-red-600 mt-1.5 flex items-start gap-1">
                          <AlertTriangle className="w-3 h-3 shrink-0 mt-0.5" />
                          {p.publish_error}
                        </p>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </ScrollArea>
          </CardContent>
        </Card>
      </div>

      <SchedulePostDialog
        post={scheduleTarget}
        open={!!scheduleTarget}
        onClose={() => setScheduleTarget(null)}
        onScheduled={handleScheduled}
      />
    </div>
  );
};
